'use client';

import { useProductTelemetryOnce } from '@/hooks/use-product-telemetry-once';
import { WarningCircle, CaretDown } from '@phosphor-icons/react';
import {
  VideoComposerLoader,
  SpeechLoader,
  MusicLoader,
  SfxLoader,
  ImageGenLoader,
  ImageSearchLoader,
  WebSearchLoader,
  GenericToolLoader,
  CapabilityActionLoader,
} from './creative-tool-loaders';
import { resolveDelegatedToolDisplay } from '@/lib/capabilities/display';
import { getToolStateTitle } from '@/lib/tool-display';

export function extractWebSearchQuery(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const query = (input as Record<string, unknown>).query;
  return typeof query === 'string' && query.trim() ? query.trim() : undefined;
}

function extractSceneCount(input: unknown): number | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const scenes = (input as Record<string, unknown>).scenes;
  if (Array.isArray(scenes)) return scenes.length;
  const effects = (input as Record<string, unknown>).effects;
  if (Array.isArray(effects)) return effects.length;
  const items = (input as Record<string, unknown>).items;
  if (Array.isArray(items)) return items.length;
  const queries = (input as Record<string, unknown>).queries;
  if (Array.isArray(queries)) return queries.length;
  return undefined;
}

function extractPrompt(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const p = input as Record<string, unknown>;
  if (typeof p.prompt === 'string') return p.prompt;
  if (typeof p.text === 'string') return p.text;
  return undefined;
}

function extractModel(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const m = (input as Record<string, unknown>).model;
  return typeof m === 'string' ? m : undefined;
}

/**
 * Themed loader per tool type. Falls back to GenericToolLoader for unknown tools.
 */
export function ToolLoader({ toolName, input }: { toolName: string; input?: unknown }) {
  const delegated = resolveDelegatedToolDisplay(toolName, input);
  const effectiveToolName = delegated.toolName;
  const effectiveInput = delegated.input ?? input;
  const query = extractWebSearchQuery(effectiveInput);
  const count = extractSceneCount(effectiveInput);
  const prompt = extractPrompt(effectiveInput);
  const model = extractModel(effectiveInput);

  switch (effectiveToolName) {
    case 'webSearch':
      return <WebSearchLoader query={query} />;

    case 'imageSearch':
      return <ImageSearchLoader query={query} />;
    case 'imageSearchBatch':
      return <ImageSearchLoader title={`Searching ${count ?? ''} image queries`} />;

    case 'generateImage':
      return <ImageGenLoader subtitle={prompt ? shortText(prompt, 60) : undefined} model={model} />;
    case 'generateImageBatch':
      return <ImageGenLoader count={count} model={model} />;

    case 'generateSpeech':
      return <SpeechLoader subtitle={prompt ? shortText(prompt, 60) : undefined} />;
    case 'generateNarrationPlan':
      return <SpeechLoader title={`Narrating ${count ?? 'all'} scenes`} subtitle="Parallel TTS with exact frame timing" />;

    case 'generateMusic':
      return <MusicLoader subtitle={prompt ? shortText(prompt, 60) : undefined} />;

    case 'generateSoundEffect':
      return <SfxLoader subtitle={prompt ? shortText(prompt, 60) : undefined} />;
    case 'generateSoundEffectBatch':
      return <SfxLoader title={`Generating ${count ?? ''} sound effects`} />;

    case 'createBrowser':
    case 'browserLiveView':
    case 'browserNavigate':
    case 'browserScroll':
    case 'browserPlaywright':
    case 'browserScreenshot':
    case 'connectBrowser':
    case 'listBrowsers':
    case 'browserListFiles':
    case 'browserReadFile':
    case 'browserWriteFile':
    case 'exportBrowserFileToSharedStorage':
    case 'importSharedFileToBrowser':
    case 'deleteBrowser':
      return <CapabilityActionLoader toolName={effectiveToolName} />;

    case 'listSandboxes':
    case 'createSandbox':
    case 'connectSandbox':
    case 'sandboxExec':
    case 'sandboxCodeRun':
    case 'sandboxSessionCreate':
    case 'sandboxSessionList':
    case 'sandboxSessionExec':
    case 'sandboxSessionLogs':
    case 'sandboxSessionDelete':
    case 'sandboxListFiles':
    case 'sandboxSearchFiles':
    case 'sandboxReadFile':
    case 'sandboxWriteFile':
    case 'sandboxCreateFolder':
    case 'sandboxDeletePath':
    case 'sandboxPreviewUrl':
    case 'exportSandboxFileToSharedStorage':
    case 'importSharedFileToSandbox':
    case 'stopSandbox':
    case 'deleteSandbox':
      return <CapabilityActionLoader toolName={effectiveToolName} />;

    case 'listGalexiaFiles':
    case 'readGalexiaFile':
    case 'readGalexiaFileLines':
    case 'searchGalexiaFiles':
    case 'writeGalexiaTextFile':
    case 'replaceGalexiaFileText':
    case 'copyGalexiaFileToWorkspace':
    case 'prepareSandboxFiles':
    case 'validateRemotionSource':
      return <CapabilityActionLoader toolName={effectiveToolName} />;

    case 'listCapabilities':
    case 'callTool':
    case 'callCapability':
    case 'reportError':
    case 'showWorkspace':
      return <CapabilityActionLoader toolName={effectiveToolName} />;

    case 'viewHistory':
      return <GenericToolLoader toolName={effectiveToolName} subtitle="Scanning compact turn summaries and tool references" />;
    case 'readTurn':
      return <GenericToolLoader toolName={effectiveToolName} subtitle="Retrieving the selected chat turn with capped output" />;
    case 'readToolResult':
      return <GenericToolLoader toolName={effectiveToolName} subtitle="Retrieving one historical tool input and output" />;

    case 'remotion': {
      const streamingCode = (() => {
        if (!effectiveInput || typeof effectiveInput !== 'object') return undefined;
        const code = (effectiveInput as Record<string, unknown>).componentCode;
        return typeof code === 'string' ? code : undefined;
      })();
      return (
        <VideoComposerLoader
          subtitle="Weaving frames, audio, and transitions"
          streamingCode={streamingCode}
        />
      );
    }

    case 'hyperframes': {
      const streamingCode = (() => {
        if (!effectiveInput || typeof effectiveInput !== 'object') return undefined;
        const document = (effectiveInput as Record<string, unknown>).htmlDocument;
        return typeof document === 'string' ? document : undefined;
      })();
      return (
        <VideoComposerLoader
          subtitle="Composing timed HTML frames"
          streamingCode={streamingCode}
        />
      );
    }

    case 'createSceneVideo':
      return <VideoComposerLoader subtitle="Assembling scene slots into a video" />;

    default:
      return <GenericToolLoader toolName={effectiveToolName} input={effectiveInput} />;
  }
}

function shortText(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

/**
 * Compact error UI for tool failures.
 */
export function ToolError({ toolName, error }: { toolName: string; error: string }) {
  useProductTelemetryOnce(
    'tool_error_displayed',
    true,
    { tool_name: toolName, failure_kind: 'tool-output-error' },
    `${toolName}:${error}`,
  );

  const friendlyName = getToolStateTitle(toolName, 'failed');

  return (
    <details className="group my-2">
      <summary className="flex items-center gap-2.5 py-2 px-3 rounded-xl bg-amber-500/[0.04] border border-amber-500/20 cursor-pointer hover:border-amber-500/30 hover:bg-amber-500/[0.06] transition-colors list-none">
        <WarningCircle weight="duotone" className="size-4 text-amber-500/75 shrink-0" />
        <span className="text-sm text-foreground/80 font-medium flex-1 truncate">
          {friendlyName} — show details
        </span>
        <CaretDown
          weight="bold"
          className="size-3.5 text-muted-foreground/50 shrink-0 transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="mt-2 p-3 rounded-xl bg-muted/20 border border-border/40">
        <p className="text-xs text-muted-foreground/80 whitespace-pre-wrap break-words font-mono leading-relaxed">
          {error}
        </p>
      </div>
    </details>
  );
}
